/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import Select, { StylesConfig } from 'react-select';
import { useAppSelector } from '../../../reducers';
import { AccessType } from '../../../types/metricGroup';
import { SurveySettings } from '../../../types/survey';
import { HandleChangeSettingsType } from '../../survey-container/SurveyContainer';
import { onChangeHandlerSelectWrapper } from '../../survey/survey-question-list-toolbar/partials/FilterQuestions';
import { ViewMap, viewOptions, ViewValues } from '../viewOptions';
import { QUESTION } from '../../../constants/terminology';

interface ViewQuestionsDropdownProps {
  sidebarSettings: SurveySettings,
  handleChangeSettings: HandleChangeSettingsType
}

const getViewOptions = () => {
  return [
    ViewValues.StandardsAndFrameworks,
    ViewValues.Sdg,
    ViewValues.Materiality,
    ViewValues.Custom
  ].map((k: ViewValues) => ViewMap[k])
};

type questionViewScopeType = (typeof viewOptions);

const reactSelectStyles: StylesConfig<{ value: string, label: string }, false> = {
  container: (provided) => ({
    ...provided,
    maxHeight: '26px',
    minWidth: '270px',
  }),
  control: (provided) => ({
    ...provided,
    maxHeight: '26px',
    minHeight: 'unset',
  }),
  singleValue: (provided) => ({ ...provided, color: '#a1a3a4' }),
  valueContainer: (provided) => ({ ...provided, position: 'unset' }),
  indicatorsContainer: (provided) => ({ ...provided, maxHeight: '26px', }),
  indicatorSeparator: (provided) => ({ ...provided, display: 'none' }),
  menu: (provider) => ({ ...provider, marginTop: '1px', }),
}

export const ViewQuestionsDropdown = (props: ViewQuestionsDropdownProps) => {

  const {
    sidebarSettings,
    handleChangeSettings,
  } = props;

  const onChange = (key: keyof Pick<SurveySettings, 'groupBy'>) => onChangeHandlerSelectWrapper(key, handleChangeSettings);

  const { loaded: customMetricGroupsLoaded, data: metricGroups } = useAppSelector((state) => state.customMetricSurveyGroups);

  const questionViewScope: questionViewScopeType = [];

  const route = useRouteMatch<{ page: string, initiativeId: string }>();
  const isDelgationPage = route?.params?.page === 'delegation';
  if (isDelgationPage) {
    questionViewScope.push({
      value: ViewValues.Users,
      label: ViewMap[ViewValues.Users].label
    });
  }

  getViewOptions().forEach(v => questionViewScope.push(v))

  if (customMetricGroupsLoaded) {
    if (metricGroups.some((g) => g.accessType === AccessType.Assigned)) {
      questionViewScope.push({
        value: ViewValues.AssignedMetrics,
        label: 'Assigned Metrics'
      });
    }
  }

  const [code] = sidebarSettings.groupBy;
  const value = questionViewScope.find(o => o.value === code);

  return (
    <Select
      styles={reactSelectStyles}
      placeholder={`Select View ${QUESTION.CAPITALIZED_PLURAL} By`}
      options={questionViewScope}
      value={value}
      onChange={onChange('groupBy')}
      isSearchable={false}
    />
  );
}
